import React, { useEffect, useState } from 'react';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { H3 } from '@osmosis/tailwind-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { text3 } from '@osmosis/tailwind-ui/styles/text';
import { faFolder } from '@fortawesome/pro-duotone-svg-icons/faFolder';
import { faFolderOpen } from '@fortawesome/pro-duotone-svg-icons/faFolderOpen';
import { faFolders } from '@fortawesome/pro-duotone-svg-icons/faFolders';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { AnimatePresence } from 'motion/react';
import LibrarySidebarItemLoggedOut from './LibrarySidebarItemLoggedOut';
import MotionDiv from '@osmosis/nextjs-components/MotionDiv';
import {
  LoggedOutProductLibraryContainsItem,
  SelectedLibraryItem,
} from '../../types/library';

type PropsSidebarAccordion = {
  l1: LoggedOutProductLibraryContainsItem;
  onSelect: (selection: LoggedOutProductLibraryContainsItem) => void;
  handleViewedLibraries: (libraryLink: string) => void;
  viewedLibraries: string[];
  index: number;
  selectedItem?: SelectedLibraryItem;
  openIndex?: number;
};

/**
 * Sidebar accordion for logged out library.
 */
const LibrarySidebarAccordion = (props: PropsSidebarAccordion) => {
  const {
    l1,
    onSelect,
    handleViewedLibraries,
    viewedLibraries,
    index,
    selectedItem,
    openIndex,
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(index === openIndex);
  }, [l1, index, openIndex]);

  const isCurrentLibraryViewed = viewedLibraries.some((link) => link === l1.id);

  // Fall back to `name` so the heading still renders when a redundant
  // `displayName` has been stripped from the payload (they are identical).
  const l1Title = l1.displayName || l1.name;

  const closedFolderIcon = isCurrentLibraryViewed ? faFolder : faFolders;

  const handleToggleOpen = () => {
    setIsOpen((prev) => !prev);
    if (isOpen) handleViewedLibraries(l1.id);
  };

  return (
    <Box>
      <Flex
        cn="group justify-between items-center p-2 my-2 cursor-pointer rounded-lg hover:bg-slate-200"
        data-testid="topic-item-btn"
        onClick={handleToggleOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggleOpen();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
      >
        <Flex cn="gap-8 items-center">
          <Box>
            {isOpen ? (
              <Icon
                className={`${text3} group-hover:text-blue-600`}
                icon={faFolderOpen}
                data-testid="opened-folder-icon"
              />
            ) : (
              <Icon
                icon={closedFolderIcon}
                className={`${text3}`}
                data-testid="closed-folder-icon"
              />
            )}
          </Box>

          <H3
            data-testid="library-sidebar-nav-subheading"
            cn="inline group-hover:text-blue-600"
            id={l1.id}
          >
            {l1Title.includes('ⓘ')
              ? l1Title.slice(0, l1Title.length - 1)
              : l1Title}
          </H3>
        </Flex>

        <Icon size="lg" icon={isOpen ? faChevronUp : faChevronDown} />
      </Flex>
      <AnimatePresence>
        {isOpen && (
          <MotionDiv
            data-testid="library-sidebar-l1-list"
            style={{ overflow: 'hidden' }}
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
          >
            {l1.contains?.map((item) => (
              <LibrarySidebarItemLoggedOut
                l1Id={l1.id}
                l2={item}
                onSelect={onSelect}
                key={item.name}
                isActive={
                  selectedItem?.l1?.id === l1.id &&
                  selectedItem?.l2.id === item.id
                }
              />
            ))}
          </MotionDiv>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default LibrarySidebarAccordion;
